<template>
  <div class="tender-letter-content-panel">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <q-spinner color="primary" size="3em" />
      <div class="q-mt-md">Loading tender letter content...</div>
    </div>

    <!-- Content -->
    <div v-else class="content-container">
      <!-- Basis of Quotation -->
      <div class="content-section">
        <BasisOfQuotationEditor
          :tender-id="tenderId"
          :items="basisItems"
          @updated="handleBasisUpdated"
        />
      </div>

      <q-separator spaced />

      <!-- General Inclusions -->
      <div class="content-section">
        <GeneralInclusionsSelector
          :tender-id="tenderId"
          :selected-list-id="inclusionsListId"
          :inclusions="inclusionsItems"
          @list-selected="handleListSelected"
          @item-toggled="handleItemToggled"
        />
      </div>

      <q-separator spaced />

      <!-- Scope Qualifications -->
      <div class="content-section">
        <ScopeQualificationsSelector
          :tender-id="tenderId"
          :qualifications="qualifications"
          @toggled="handleQualificationToggled"
          @custom-added="handleCustomAdded"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import BasisOfQuotationEditor from './BasisOfQuotationEditor.vue';
  import GeneralInclusionsSelector from './GeneralInclusionsSelector.vue';
  import ScopeQualificationsSelector from './ScopeQualificationsSelector.vue';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
  });

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const basisItems = ref([]);
  const inclusionsListId = ref(null);
  const inclusionsItems = ref([]);
  const qualifications = ref([]);

  // Methods
  async function loadLetterContent() {
    loading.value = true;
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/letter-content`);
      const data = await response.json();

      if (data.success) {
        const content = data.data;

        // Set basis of quotation items
        basisItems.value = content.basis_of_quotation || [];

        // Set general inclusions
        if (content.general_inclusions) {
          inclusionsListId.value = content.general_inclusions.list_id || null;
          inclusionsItems.value = content.general_inclusions.items || [];
        }

        // Set scope qualifications
        qualifications.value = content.scope_qualifications || [];
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to load letter content: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      loading.value = false;
    }
  }

  function handleBasisUpdated(updatedData) {
    if (updatedData.basis_of_quotation) {
      basisItems.value = updatedData.basis_of_quotation;
    }
  }

  function handleListSelected(data) {
    if (data) {
      inclusionsListId.value = data.selected_inclusions_list_id || null;
      // Extract items array from the general_inclusions object
      inclusionsItems.value = (data.general_inclusions && data.general_inclusions.items) || [];
    } else {
      inclusionsListId.value = null;
      inclusionsItems.value = [];
    }
  }

  function handleItemToggled(data) {
    // Update the local inclusions items with the toggled item
    if (data && data.general_inclusions && data.general_inclusions.items) {
      inclusionsItems.value = data.general_inclusions.items;
    }
  }

  function handleQualificationToggled(data) {
    // Update the local qualifications with the toggled qualification
    if (data && data.scope_qualifications) {
      qualifications.value = data.scope_qualifications;
    }
  }

  function handleCustomAdded(newQualification) {
    // Reload to get the updated list
    loadLetterContent();
  }

  // Lifecycle
  onMounted(() => {
    loadLetterContent();
  });
</script>

<style scoped>
.tender-letter-content-panel {
  width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 16px;
  color: #666;
}

.content-container {
  width: 100%;
}

.content-section {
  margin-bottom: 16px;
}

.content-section:last-child {
  margin-bottom: 0;
}
</style>
